import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  FormControl,
  FormLabel,
  FormControlLabel,
  RadioGroup,
  Radio,
  Typography,
  Card,
  CardContent,
  Alert,
  Chip,
  FormHelperText
} from '@mui/material';
import {
  Schedule as ScheduleIcon,
  AttachMoney as MoneyIcon,
  DateRange as DateIcon,
  Star as PriorityIcon
} from '@mui/icons-material';

/**
 * TimelineBudgetStep Component - User Story 2
 * Fourth step in the onboarding wizard for collecting timeline and budget information
 * 
 * Following VegaDigital Constitution: WCAG 2.1 AA compliance, comprehensive validation
 */

interface TimelineBudgetData {
  desired_timeline?: string;
  preferred_start_date?: string;
  target_launch_date?: string;
  budget_range?: string;
  budget_flexibility?: string;
  payment_preference?: string;
  priority_factors?: string[];
  rush_requirements?: string;
  milestone_preferences?: string;
  additional_notes?: string;
}

interface TimelineBudgetStepProps {
  data: TimelineBudgetData;
  onChange: (data: TimelineBudgetData) => void;
  onValidationChange: (isValid: boolean) => void;
  errors?: Record<string, string[]>;
}

const TIMELINE_OPTIONS = [
  { value: 'urgent', label: 'ASAP (Rush Project)', description: '2-4 weeks', icon: '🚀' },
  { value: 'fast', label: 'Fast Track', description: '1-2 months', icon: '⚡' },
  { value: 'standard', label: 'Standard Timeline', description: '2-4 months', icon: '📅' },
  { value: 'relaxed', label: 'Flexible Timeline', description: '4-6 months', icon: '🎯' },
  { value: 'long-term', label: 'Long-term Project', description: '6+ months', icon: '🏗️' }
];

const BUDGET_RANGES = [
  { value: 'micro', label: 'Micro Project', range: '$1,000 - $5,000', description: 'Small features or simple sites' },
  { value: 'small', label: 'Small Project', range: '$5,000 - $15,000', description: 'Basic websites or applications' },
  { value: 'medium', label: 'Medium Project', range: '$15,000 - $50,000', description: 'Custom applications with moderate complexity' },
  { value: 'large', label: 'Large Project', range: '$50,000 - $150,000', description: 'Enterprise applications or complex systems' },
  { value: 'enterprise', label: 'Enterprise Project', range: '$150,000+', description: 'Large-scale, complex enterprise solutions' },
  { value: 'discuss', label: 'Prefer to Discuss', range: 'Custom Quote', description: 'Let\'s discuss your specific needs' }
];

const BUDGET_FLEXIBILITY = [
  { value: 'strict', label: 'Strict Budget', description: 'Must stay within the specified range' },
  { value: 'moderate', label: 'Moderate Flexibility', description: 'Can go 10-20% over for important features' },
  { value: 'flexible', label: 'Flexible', description: 'Budget can increase for valuable additions' },
  { value: 'value-focused', label: 'Value-Focused', description: 'Willing to invest more for quality and results' }
];

const PAYMENT_PREFERENCES = [
  { value: 'milestone', label: 'Milestone-Based', description: 'Pay based on project milestones' },
  { value: 'monthly', label: 'Monthly Payments', description: 'Regular monthly payments' },
  { value: 'upfront-50', label: '50% Upfront', description: '50% upfront, 50% on completion' },
  { value: 'completion', label: 'Payment on Completion', description: 'Full payment upon project completion' },
  { value: 'discuss', label: 'Open to Discussion', description: 'Flexible on payment terms' }
];

const PRIORITY_FACTORS = [
  { value: 'quality', label: 'Quality & Reliability', icon: '⭐' },
  { value: 'speed', label: 'Speed to Market', icon: '🚀' },
  { value: 'cost', label: 'Cost Efficiency', icon: '💰' },
  { value: 'features', label: 'Feature Completeness', icon: '🔧' },
  { value: 'scalability', label: 'Future Scalability', icon: '📈' },
  { value: 'support', label: 'Ongoing Support', icon: '🤝' }
];

export const TimelineBudgetStep: React.FC<TimelineBudgetStepProps> = ({
  data,
  onChange,
  onValidationChange,
  errors = {}
}) => {
  const [localData, setLocalData] = useState<TimelineBudgetData>(data);
  const [localErrors, setLocalErrors] = useState<Record<string, string>>({});

  // Combined validation and parent update effect
  useEffect(() => {
    const newErrors: Record<string, string> = {};

    if (!localData.desired_timeline) {
      newErrors.desired_timeline = 'Please select a timeline preference';
    }

    if (!localData.budget_range) {
      newErrors.budget_range = 'Please select a budget range';
    }

    if (localData.preferred_start_date && localData.target_launch_date) {
      const startDate = new Date(localData.preferred_start_date);
      const launchDate = new Date(localData.target_launch_date);
      if (startDate >= launchDate) {
        newErrors.target_launch_date = 'Launch date must be after start date';
      }
    }

    // Only update errors if they changed
    const errorsChanged = JSON.stringify(newErrors) !== JSON.stringify(localErrors);
    if (errorsChanged) {
      setLocalErrors(newErrors);
    }
    
    const isValid = Object.keys(newErrors).length === 0 && 
                   !!localData.desired_timeline &&
                   !!localData.budget_range;
    
    // Update parent with data and validation
    onChange(localData);
    onValidationChange(isValid);
  }, [localData, localErrors, onChange, onValidationChange]);

  const handlePriorityChange = (factor: string, checked: boolean) => {
    setLocalData(prev => ({
      ...prev,
      priority_factors: checked 
        ? [...(prev.priority_factors || []), factor]
        : (prev.priority_factors || []).filter(p => p !== factor)
    }));
  };

  const getError = (field: string): string => {
    return errors[field]?.[0] || localErrors[field] || '';
  };

  const hasError = (field: string): boolean => {
    return !!(errors[field]?.length || localErrors[field]);
  };

  const selectedTimeline = TIMELINE_OPTIONS.find(option => option.value === localData.desired_timeline);
  const selectedBudget = BUDGET_RANGES.find(option => option.value === localData.budget_range);

  return (
    <Box>
      <Typography variant="h6" gutterBottom color="primary">
        Timeline & Budget
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Help us understand your timeline and budget requirements to provide accurate estimates and planning.
      </Typography>

      {/* Project Timeline */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ScheduleIcon color="primary" />
          Project Timeline *
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          {TIMELINE_OPTIONS.map((option) => (
            <Card
              key={option.value}
              sx={{
                cursor: 'pointer',
                minWidth: 200,
                border: localData.desired_timeline === option.value ? 2 : 1,
                borderColor: localData.desired_timeline === option.value ? 'primary.main' : 'divider',
                '&:hover': { borderColor: 'primary.main' }
              }}
              onClick={() => setLocalData(prev => ({ ...prev, desired_timeline: option.value }))}
              data-testid={`timeline-${option.value}`}
            >
              <CardContent sx={{ textAlign: 'center', p: 2 }}>
                <Typography variant="h4" sx={{ mb: 1 }}>
                  {option.icon}
                </Typography>
                <Typography variant="subtitle2" gutterBottom>
                  {option.label}
                </Typography>
                <Typography variant="body2" color="primary" sx={{ fontWeight: 'bold' }}>
                  {option.description}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Box>
        {hasError('desired_timeline') && (
          <FormHelperText error sx={{ mt: 1 }}>
            {getError('desired_timeline')}
          </FormHelperText>
        )}
      </Box>

      {/* Date Preferences */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DateIcon color="primary" />
          Important Dates
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)' } }}>
            <TextField
              fullWidth
              label="Preferred Start Date"
              type="date"
              value={localData.preferred_start_date || ''}
              onChange={(e) => setLocalData(prev => ({ ...prev, preferred_start_date: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              helperText="When would you like to start the project?"
              inputProps={{ 'data-testid': 'start-date-input' }}
            />
          </Box>
          <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)' } }}>
            <TextField
              fullWidth
              label="Target Launch Date"
              type="date"
              value={localData.target_launch_date || ''}
              onChange={(e) => setLocalData(prev => ({ ...prev, target_launch_date: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              error={hasError('target_launch_date')}
              helperText={getError('target_launch_date') || 'When do you need the project completed?'}
              inputProps={{ 'data-testid': 'launch-date-input' }}
            />
          </Box>
        </Box>
      </Box>

      {/* Budget Range */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MoneyIcon color="primary" />
          Budget Range *
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          {BUDGET_RANGES.map((option) => (
            <Card
              key={option.value}
              sx={{
                cursor: 'pointer',
                minWidth: 250,
                border: localData.budget_range === option.value ? 2 : 1,
                borderColor: localData.budget_range === option.value ? 'primary.main' : 'divider',
                '&:hover': { borderColor: 'primary.main' }
              }}
              onClick={() => setLocalData(prev => ({ ...prev, budget_range: option.value }))}
              data-testid={`budget-${option.value}`}
            >
              <CardContent sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  {option.label}
                </Typography>
                <Typography variant="body2" color="primary" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {option.range}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {option.description}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Box>
        {hasError('budget_range') && (
          <FormHelperText error sx={{ mt: 1 }}>
            {getError('budget_range')}
          </FormHelperText>
        )}
      </Box>

      {/* Budget Flexibility */}
      <Box sx={{ mb: 4 }}>
        <FormControl component="fieldset" fullWidth>
          <FormLabel component="legend">
            <Typography variant="subtitle1">Budget Flexibility</Typography>
          </FormLabel>
          <RadioGroup
            value={localData.budget_flexibility || ''}
            onChange={(e) => setLocalData(prev => ({ ...prev, budget_flexibility: e.target.value }))}
            sx={{ mt: 1 }}
          >
            {BUDGET_FLEXIBILITY.map((option) => (
              <FormControlLabel
                key={option.value}
                value={option.value}
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body2">{option.label}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.description}
                    </Typography>
                  </Box>
                }
                data-testid={`flexibility-${option.value}`}
              />
            ))}
          </RadioGroup>
        </FormControl>
      </Box>

      {/* Payment Preference */}
      <Box sx={{ mb: 4 }}>
        <FormControl component="fieldset" fullWidth>
          <FormLabel component="legend">
            <Typography variant="subtitle1">Payment Preference</Typography>
          </FormLabel>
          <RadioGroup
            value={localData.payment_preference || ''}
            onChange={(e) => setLocalData(prev => ({ ...prev, payment_preference: e.target.value }))}
            sx={{ mt: 1 }}
          >
            {PAYMENT_PREFERENCES.map((option) => (
              <FormControlLabel
                key={option.value}
                value={option.value}
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body2">{option.label}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.description}
                    </Typography>
                  </Box>
                }
                data-testid={`payment-${option.value}`}
              />
            ))}
          </RadioGroup>
        </FormControl>
      </Box>

      {/* Priority Factors */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PriorityIcon color="primary" />
          What's Most Important to You? (Select up to 3)
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {PRIORITY_FACTORS.map((factor) => (
            <Card
              key={factor.value}
              sx={{
                cursor: 'pointer',
                minWidth: 180,
                border: localData.priority_factors?.includes(factor.value) ? 2 : 1,
                borderColor: localData.priority_factors?.includes(factor.value) ? 'primary.main' : 'divider',
                '&:hover': { borderColor: 'primary.main' },
                opacity: (localData.priority_factors?.length || 0) >= 3 && !localData.priority_factors?.includes(factor.value) ? 0.5 : 1
              }}
              onClick={() => {
                const isSelected = localData.priority_factors?.includes(factor.value);
                const currentCount = localData.priority_factors?.length || 0;
                
                if (!isSelected && currentCount >= 3) return; // Don't allow more than 3
                
                handlePriorityChange(factor.value, !isSelected);
              }}
              data-testid={`priority-${factor.value}`}
            >
              <CardContent sx={{ textAlign: 'center', p: 2 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  {factor.icon}
                </Typography>
                <Typography variant="body2">
                  {factor.label}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Box>
        {(localData.priority_factors?.length || 0) >= 3 && (
          <Alert severity="info" sx={{ mt: 2 }}>
            You've selected the maximum of 3 priorities. To select a different priority, first deselect one of your current choices.
          </Alert>
        )}
      </Box>

      {/* Rush Requirements */}
      {localData.desired_timeline === 'urgent' && (
        <Box sx={{ mb: 4 }}>
          <TextField
            fullWidth
            label="Rush Project Requirements"
            value={localData.rush_requirements || ''}
            onChange={(e) => setLocalData(prev => ({ ...prev, rush_requirements: e.target.value }))}
            helperText="Please explain why this project needs to be completed urgently and any specific constraints"
            multiline
            rows={3}
            required
            inputProps={{
              'aria-label': 'Rush Project Requirements',
              'data-testid': 'rush-requirements-input'
            }}
          />
        </Box>
      )}

      {/* Additional Notes */}
      <Box sx={{ mb: 4 }}>
        <TextField
          fullWidth
          label="Additional Notes about Timeline & Budget"
          value={localData.additional_notes || ''}
          onChange={(e) => setLocalData(prev => ({ ...prev, additional_notes: e.target.value }))}
          helperText="Any additional information about your timeline or budget constraints"
          multiline
          rows={3}
          inputProps={{
            'aria-label': 'Additional Notes',
            'data-testid': 'additional-notes-input'
          }}
        />
      </Box>

      {/* Summary */}
      {(selectedTimeline || selectedBudget) && (
        <Box sx={{ p: 3, bgcolor: 'primary.50', borderRadius: 2, border: '1px solid', borderColor: 'primary.200' }}>
          <Typography variant="subtitle1" gutterBottom color="primary">
            Project Summary
          </Typography>
          {selectedTimeline && (
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Timeline:</strong> {selectedTimeline.label} ({selectedTimeline.description})
            </Typography>
          )}
          {selectedBudget && (
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Budget:</strong> {selectedBudget.label} - {selectedBudget.range}
            </Typography>
          )}
          {localData.priority_factors?.length && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Top Priorities:</strong>
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {localData.priority_factors.map((factor) => {
                  const factorData = PRIORITY_FACTORS.find(f => f.value === factor);
                  return (
                    <Chip
                      key={factor}
                      label={`${factorData?.icon} ${factorData?.label}`}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  );
                })}
              </Box>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};